import './style.scss';

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';

import { getAll as getAllPrograms } from 'apis/programs.api';
import {
  deleteById,
  downloadZip,
  exportExcel,
  getAllResearchTypes,
  getAllStatuses,
  restoreById,
  sendRemindEmail,
} from 'apis/projectProposals.api';
import { approveById, getAll as getAllProjectProposals } from 'apis/pubProjectProposals.api';

import { getAllResearchFieldsByUserId } from 'apis/researchFields.api';

import { Confirm, ViewEmailLogs, ViewHistory } from 'features/RevisePubProjectProposal/components';
import SearchFilters from 'features/RevisePubProjectProposal/components/SearchFilters';
import SendMail from 'features/RevisePubProjectProposal/components/SendMail';

import { AbilityContext, Can } from 'components/Can';
import ErrorDialog from 'components/ErrorDialog';
import Pagination from 'components/Pagination';
import { TOAST } from 'constant';
import RequestUpdateForm from 'features/RevisePubProjectProposal/components/RequestUpdateForm';
import CreateContractForm from 'features/RevisePubProjectProposal/components/CreateContractForm';
// import { defaultProjecProposalsFilters } from 'features/ReviseProjectProposal/defaultValues';
import { toast } from 'layout';
import { mapError } from 'utils/func';
import useSelectionChange from 'utils/hooks/useSelectionChange';
import { totalExpenditureSchema } from 'features/RevisePubProjectProposal/validation';

function RevisePubProjectProposalViewList() {
  // #region data
  const { t } = useTranslation();

  const location = useLocation();
  const [searchParams] = useSearchParams({ replace: true });
  const dialogSendMailRef = useRef();
  const dialogConfirmRef = useRef();
  const dialogRequestUpdateRef = useRef();
  const dialogCreateContractRef = useRef();
  const approvalMenu = useRef(null);
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });
  const [viewHistoryVisible, setViewHistoryVisible] = useState(false);
  const [projectHistory, setProjectHistory] = useState([]);
  const [viewEmailLogsVisible, setViewEmailLogsVisible] = useState(false);
  const [projectProposalId, setProjectProposalId] = useState('');
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [showMode, setShowMode] = useState('hideDeletedProjects');
  const ability = useContext(AbilityContext);
  const [isVisibleSearchFilters, setIsVisibleSearchFilters] = useState(false);
  const [filtersTable, setFiltersTable] = useState({
    _vietnameseName: { value: null },
    _projectType: { value: null },
    _ownerName: { value: null },
    // _organization: { value: null },
    _researchField: { value: null },
    _researchType: { value: null },
    _totalExpenditure: { value: null },
    _program: { value: null },
    _submittedAt: { value: null },
    // _councilType: { value: null },
    _status: { value: null },
  });

  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    vietnameseName: null,
    projectType: null,
    ownerName: null,
    // organizationId: null,
    researchFieldId: null,
    researchTypeId: null,
    totalExpenditure: { min: null, max: null },
    programId: null,
    submittedAt: { start: null, end: null },
    // councilTypeId: null,
    statusName: null,
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    vietnameseName: null,
    projectType: null,
    ownerName: null,
    // organizationId: null,
    researchFieldId: null,
    researchTypeId: null,
    totalExpenditure: { min: null, max: null },
    programId: null,
    submittedAt: { start: null, end: null },
    // councilTypeId: null,
    statusName: null,
  });

  const projectTypes = [
    { value: 'A', name: t('projectTypes.A') },
    { value: 'B', name: t('projectTypes.B') },
    { value: 'C', name: t('projectTypes.C') },
    { value: 'CQG', name: t('projectTypes.CQG') },
  ];

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ['pubProjectProposals', pagination, appliedFilters, showMode],
    ({ queryKey: [, _pagination, _filters, _showMode] }) =>
      getAllProjectProposals(_pagination, _filters, _showMode)
  );
  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_data]
  );

  const { data: researchFieldsData } = useQuery(
    ['pubProjectProposalResearchFieldsByUserId'],
    getAllResearchFieldsByUserId
  );
  const researchFields = useMemo(
    () =>
      researchFieldsData?.data.map((e) => ({
        id: e.id,
        name: e?.code ? t(`researchFields.${e.code}`) : e.name,
      })) ?? [],
    [researchFieldsData, t]
  );

  const { data: researchTypesData } = useQuery(
    ['pubProjectProposalResearchTypes'],
    getAllResearchTypes
  );
  const researchTypes = useMemo(
    () =>
      researchTypesData?.data?.map((e) => ({
        id: e.id,
        name: e?.code ? t(`researchTypes.${e.code}`) : e.name,
      })) ?? [],
    [researchTypesData, t]
  );

  const { data: programData } = useQuery(['program'], getAllPrograms);
  const programs = useMemo(() => programData?.data ?? [], [programData]);

  const { data: statusesData } = useQuery(['pubProjectProposalStatuses'], getAllStatuses);
  const statuses = useMemo(
    () =>
      statusesData?.data.map((e) => ({
        id: e.id,
        code: e?.name,
        name: e?.name ? t(`statuses.${e.name}`) : '',
      })) ?? [],
    [statusesData, t]
  );

  const {
    mutate: mutateExportExcel,
    isLoading: isExportExcelLoading,
    isError: isExportExcelError,
    error: exportExcelError,
  } = useMutation(exportExcel);

  const {
    mutate: mutateDownloadZip,
    isLoading: isDownloadZipLoading,
    isError: isDownloadZipError,
    error: downloadZipError,
  } = useMutation(downloadZip);

  const {
    mutate: mutateSendRemindEmail,
    isLoading: isSendRemindEmailLoading,
    isError: isSendRemindEmailError,
    error: sendRemindEmailError,
  } = useMutation(sendRemindEmail);

  const { mutate: mutateApprove } = useMutation(approveById);
  const { mutate: mutateDelete } = useMutation(deleteById);
  const { mutate: mutateRestore } = useMutation(restoreById);

  const {
    selectedItems,
    // eslint-disable-next-line no-unused-vars
    unselectedItems,
    isSelectedAll,
    isUnselectedSome,
    handleAllRowSelect,
    handleAllRowUnselect,
    handleRowSelect,
    handleRowUnselect,
  } = useSelectionChange(data, pagination);

  const approvalItems = [
    {
      label: t('pubProjectProposal.viewList.approve'),
      icon: 'pi pi-check',
      command: () => {},
    },
    { separator: true },
    {
      label: t('pubProjectProposal.viewList.changeRequest'),
      icon: 'pi pi-refresh',
      command: () => {},
    },
  ];

  // #endregion Data

  // #region Event
  useEffect(() => {
    setShowMode(showMode);
  }, [t]);

  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    );

    const modifySubmittedAt = {};
    if (localFilters?.submittedAt) {
      const { start, end } = localFilters.submittedAt;
      if (start || end) {
        modifySubmittedAt.start = start ? new Date(start) : '';
        modifySubmittedAt.end = end ? new Date(end) : '';
      }
    }

    setFilters({
      ...localFilters,
      submittedAt: modifySubmittedAt,
      totalExpenditure: { min: undefined, max: undefined },
      // statusName: ['open'],
    });
    setAppliedFilters({
      ...localFilters,
      submittedAt: modifySubmittedAt,
      totalExpenditure: { min: undefined, max: undefined },
      // statusName: ['open'],
    });

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
  }, []);

  const handleExportExcel = () => {
    // Call api export excel
    // param:selectedItems,unselectedItems,isSelectedAll,isUnselectedSome,researchFieldId,statusName
    mutateExportExcel(
      {
        selectedItems: selectedItems.map((e) => e.id),
        unselectedItems: unselectedItems.map((e) => e.id),
        isSelectedAll,
        isUnselectedSome,
        ...filters,
      },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = t('pubProjectProposal.viewList.excelName');

            link.click();
          }
          handleAllRowUnselect();
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };

  const handleDownloadZip = () => {
    // Call api download files
    // param:selectedItems,unselectedItems,isSelectedAll,isUnselectedSome,researchFieldId,statusName
    mutateDownloadZip(
      {
        selectedItems: selectedItems.map((e) => e.id),
        unselectedItems: unselectedItems.map((e) => e.id),
        isSelectedAll,
        isUnselectedSome,
        ...filters,
      },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const contentDisposition = res?.headers['content-disposition'];
            const filenameMatch = contentDisposition?.match(/filename='(.+)'/);
            const filename = filenameMatch ? filenameMatch[1] : 'Danh sach de tai DHQG.zip';
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = filename;

            link.click();
          }
          handleAllRowUnselect();
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };

  const handleSendMail = (subject, content, _reset, _setVisible) => {
    mutateSendRemindEmail(
      {
        subject,
        content,
        selectedItems: selectedItems.map((e) => e.id),
        unselectedItems: unselectedItems.map((e) => e.id),
        isSelectedAll,
        isUnselectedSome,
        ...filters,
      },
      {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('pubProjectProposal.sendRemindEmailSuccessMessage'));
          handleAllRowUnselect();
          _reset();
          _setVisible(false);
          refetch();
          // reset to empty so that when click view emailLogs again, it refetch emailLogs data
          setProjectProposalId('');
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };

  // #endregion Event

  // #region Render
  const isRowSelectable = (event) => event.data.deletedAt === null;

  const rowClassName = (value) => (value?.deletedAt !== null ? 'p-disabled' : '');

  const formatName = (value) => {
    if (value?.deletedAt === null) {
      return (
        <Link to={`${value?.reference}?tab=0`} className="hover:underline">
          {value?.vietnameseName}
        </Link>
      );
    }

    return <span>{value?.vietnameseName}</span>;
  };
  const vietnameseNameFilter = () => (
    <InputText
      value={filters.vietnameseName}
      onChange={(e) => {
        const newFilters = { ...filters, vietnameseName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('pubProjectProposal.viewList.name')}
      className="p-column-filter"
    />
  );

  const projectTypeFilter = () => (
    <MultiSelect
      value={filters.projectType}
      onChange={(e) => {
        const newFilters = { ...filters, projectType: e.value };
        setFilters(newFilters);
      }}
      options={projectTypes}
      optionLabel="name"
      optionValue="value"
      filter
      showClear
      placeholder={t('pubProjectProposal.viewList.projectType')}
      maxSelectedLabels={3}
      style={{ maxWidth: '16rem', width: '12.5rem' }}
    />
  );

  const formatOwner = (value) => {
    const owner = value?.partner;
    if (owner) return <div>{owner?.name}</div>;
    return null;
  };
  const ownerNameFilter = () => (
    <InputText
      value={filters.ownerName}
      onChange={(e) => {
        const newFilters = { ...filters, ownerName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('pubProjectProposal.viewList.owner')}
      className="p-column-filter"
    />
  );
  // const formatOrganization = (value) =>
  //   t('lang') === 'vi'
  //     ? value?.projectProposalOrganizations[0]?.name
  //     : value?.projectProposalOrganizations[0]?.englishName;
  // const organizationFilter = () => (
  //   <MultiSelect
  //     value={filters.organizationId}
  //     onChange={(e) => {
  //       const newFilters = { ...filters, organizationId: e.value };
  //       setFilters(newFilters);
  //     }}
  //     options={organizations}
  //     optionLabel="name"
  //     optionValue="id"
  //     filter
  //     showClear
  //     placeholder={t('pubProjectProposal.viewList.organization')}
  //     maxSelectedLabels={3}
  //     style={{ maxWidth: '22rem', width: '18.5rem' }}
  //   />
  // );

  const formatDate = (date) => (date ? new Date(date).toLocaleDateString('en-GB') : '');

  const formatSubmittedAt = (value) => formatDate(value?.submittedAt);

  const formatResearchFields = (value) => (
    <div>
      {value?.projectProposalResearchFields
        ?.sort((a, b) => {
          if (a.priority === null) return 1;
          if (b.priority === null) return -1;
          if (a.priority === b) return 0;
          return a.priority < b.priority ? -1 : 1;
        })
        ?.map((researchField, index) => (
          <p key={researchField.researchField.id} className="text-left m-0">
            {`(${researchField?.priority ?? index + 1}) ${t(
              `researchFields.${researchField.researchField.code}`
            )}`}
          </p>
        ))}
    </div>
  );
  const researchFieldFilter = () => (
    <MultiSelect
      value={filters.researchFieldId}
      onChange={(e) => {
        const newFilters = { ...filters, researchFieldId: e.value };
        setFilters(newFilters);
      }}
      options={researchFields}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('pubProjectProposal.viewList.researchField')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const formatResearchType = (value) =>
    value?.researchType?.id ? t(`researchTypes.${value?.researchType.code}`) : '';
  const researchTypeFilter = () => (
    <MultiSelect
      value={filters.researchTypeId}
      onChange={(e) => {
        const newFilters = { ...filters, researchTypeId: e.value };
        setFilters(newFilters);
      }}
      options={researchTypes}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('pubProjectProposal.viewList.projectType')}
      maxSelectedLabels={3}
      style={{ maxWidth: '16rem', width: '12.5rem' }}
    />
  );

  const formatTotalExpenditure = (value) => {
    if (value?.totalExpenditure === 0 && value?.proposedExpenditureSummaries?.length > 0) {
      const total = value?.proposedExpenditureSummaries.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromFunding = cur?.expenditureFromFunding ?? 0;
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromFunding + expenditureFromVNUHCM;
        }
        return sum;
      }, 0);
      return total?.toLocaleString('vi');
    }
    return value?.totalExpenditure?.toLocaleString('vi');
  };
  const totalExpenditureFilter = () => (
    <div>
      <div className="flex align-items-center mb-2">
        <span className="w-3">{t('pubProjectProposal.viewList.from')}</span>
        <InputNumber
          value={filters?.totalExpenditure?.min ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              totalExpenditure: { ...filters.totalExpenditure, min: e.value },
            };
            setFilters(newFilters);
          }}
          className={classNames('currency', {
            'p-invalid': !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            }),
          })}
          tooltip={
            !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            })
              ? t('pubProjectProposal.viewList.validation.min')
              : ''
          }
          tooltipOptions={{ position: 'bottom' }}
          mode="currency"
          currency="VND"
          locale="vi-VN"
          useGrouping
        />
      </div>
      <div className="flex align-items-center">
        <span className="w-3">{t('pubProjectProposal.viewList.to')}</span>
        <InputNumber
          value={filters?.totalExpenditure?.max ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              totalExpenditure: { ...filters.totalExpenditure, max: e.value },
            };
            setFilters(newFilters);
          }}
          className={classNames('currency', {
            'p-invalid': !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            }),
          })}
          tooltip={
            !totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            })
              ? t('pubProjectProposal.viewList.validation.max')
              : ''
          }
          tooltipOptions={{ position: 'bottom' }}
          mode="currency"
          currency="VND"
          locale="vi-VN"
          useGrouping
        />
      </div>
    </div>
  );

  const programFilter = () => (
    <MultiSelect
      value={filters.programId}
      onChange={(e) => {
        const newFilters = { ...filters, programId: e.value };
        setFilters(newFilters);
      }}
      options={programs}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('pubProjectProposal.viewList.program')}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const submittedAtFilter = () => (
    <div>
      <div className="flex align-items-center mb-2">
        <span className="w-2">{t('pubProjectProposal.viewList.from')}</span>
        <Calendar
          showIcon
          showButtonBar
          value={filters?.submittedAt?.start ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              submittedAt: { ...filters.submittedAt, start: e.value },
            };
            setFilters(newFilters);
          }}
          dateFormat="dd/mm/yy"
          locale={t('lang') !== 'lang' ? t('lang') : 'vi'}
        />
      </div>
      <div className="flex align-items-center">
        <span className="w-2">{t('pubProjectProposal.viewList.to')}</span>
        <Calendar
          showIcon
          showButtonBar
          value={filters?.submittedAt?.end ?? undefined}
          onChange={(e) => {
            const newFilters = {
              ...filters,
              submittedAt: { ...filters.submittedAt, end: e.value },
            };
            setFilters(newFilters);
          }}
          dateFormat="dd/mm/yy"
          locale={t('lang') !== 'lang' ? t('lang') : 'vi'}
        />
      </div>
    </div>
  );

  // const formatCouncil = (value) => {
  //   const projectProposalCouncils = value?.projectProposalCouncils ?? []; // sorted ASC by councilType.order

  //   if (projectProposalCouncils?.length > 0) {
  //     return projectProposalCouncils?.map((ppCouncil, index) => {
  //       const correspondingReviseLog = (value?.projectProposalReviseLogs ?? [])?.find(
  //         (item) => item?.type === councilTypeToReviseLogTypeMap?.[ppCouncil?.councilType?.code]
  //       );
  //       const hasRequest = !!ppCouncil?.councilStatus && !!correspondingReviseLog;

  //       // 7. có yc giải trình, chủ nhiệm đã giải trình và CHỐT
  //       // TODO

  //       let requestStatusIcon = {};
  //       // có yc giải trình
  //       if (hasRequest) {
  //         // 4. có yc giải trình, chủ nhiệm chưa giải trình
  //         if (correspondingReviseLog?.isOpened && !correspondingReviseLog?.ownerComment) {
  //           requestStatusIcon = {
  //             tooltip: t('pubProjectProposal.requestExplanation.statuses.ownerNotAddExplanationYet'),
  //             className: 'text-red-600',
  //             icon: 'pi pi-times-circle',
  //           };
  //         }
  //         // 5. có yc giải trình, chủ nhiệm đã giải trình
  //         if (!correspondingReviseLog?.isOpened && correspondingReviseLog?.ownerComment) {
  //           requestStatusIcon = {
  //             tooltip: t('pubProjectProposal.requestExplanation.statuses.ownerHasAddedExplanation'),
  //             className: 'text-green-600',
  //             icon: 'pi pi-check-circle',
  //           };
  //         }
  //         // 6. có yc giải trình, chủ nhiệm đã giải trình, cv yêu cầu gtrinh lại
  //         if (correspondingReviseLog?.isOpened && correspondingReviseLog?.ownerComment) {
  //           requestStatusIcon = {
  //             tooltip: t(
  //               'pubProjectProposal.requestExplanation.statuses.ownerNotUpdateExplanationYet'
  //             ),
  //             className: 'text-orange-500',
  //             icon: 'pi pi-replay',
  //           };
  //         }
  //       }
  //       return (
  //         <div key={ppCouncil?.id ?? index} className="flex flex-column align-items-start">
  //           <span className="my-1">- {ppCouncil?.councilType?.name}</span>
  //           <div className="flex flex-wrap align-items-center">
  //             <Tag
  //               style={{
  //                 maxWidth: '8rem',
  //                 minWidth: '4rem',
  //                 textAlign: 'center',
  //               }}
  //               severity={councilStatusSeverityMap[ppCouncil?.councilStatus ?? 'notReviewed']}
  //               value={t(`council.viewList.${ppCouncil?.councilStatus ?? 'notReviewed'}`)}
  //               rounded
  //             />

  //             {hasRequest && (
  //               <>
  //                 <i
  //                   className={`request-${ppCouncil?.id} pi pi-file p-overlay-badge ml-1 text-xl`}
  //                   data-pr-tooltip={
  //                     requestStatusIcon?.tooltip ??
  //                     t(
  //                       'pubProjectProposal.requestExplanation.statuses.specialistNotRequestExplanationYet'
  //                     )
  //                   }
  //                 >
  //                   <Badge
  //                     value={<i className={`${requestStatusIcon?.icon} font-bold`} />}
  //                     className={requestStatusIcon?.className}
  //                     style={{ backgroundColor: 'transparent' }}
  //                   />
  //                 </i>
  //                 <Tooltip target={`.request-${ppCouncil?.id}`} />
  //               </>
  //             )}
  //           </div>
  //         </div>
  //       );
  //     });
  //   }
  //   return null;
  // };
  // const councilTypeFilter = () => (
  //   <MultiSelect
  //     value={filters.councilTypeId}
  //     onChange={(e) => {
  //       const newFilters = { ...filters, councilTypeId: e.value };
  //       setFilters(newFilters);
  //     }}
  //     options={councilTypes}
  //     optionLabel="name"
  //     optionValue="id"
  //     filter
  //     showClear
  //     placeholder={t('pubProjectProposal.viewList.councilType')}
  //     style={{ maxWidth: '22rem', width: '18.5rem' }}
  //   />
  // );

  const formatStatus = (value) => {
    const statusColorMapping = {
      pendingReview: 'info',
      pendingChange: 'warning',
      new: 'info',
      approved: 'success',
    };

    return (
      <Tag
        style={{ maxWidth: '8rem' }}
        severity={statusColorMapping[value?.status.name]}
        value={t(`statuses.${value?.status.name}`)}
        rounded
      />
    );
  };
  const statusFilter = () => (
    <MultiSelect
      value={filters.statusName}
      options={statuses}
      onChange={(e) => {
        const newFilters = { ...filters, statusName: e.value };
        setFilters(newFilters);
      }}
      optionLabel="name"
      optionValue="code"
      showClear
      filter
      placeholder={t('pubProjectProposal.viewList.status')}
      style={{ minWidth: '12rem' }}
      maxSelectedLabels={3}
    />
  );

  const formatActions = (value) => (
    <div
      className="flex gap-2 flex-wrap justify-content-center"
      style={{ width: '7rem', maxWidth: '7rem' }}
    >
      {/* eslint-disable indent */}
      {value?.deletedAt === null ? (
        <>
          {/* Giải trình chỉnh sửa trước sơ tuyển (giải trình ở đơn vị) */}
          {/* {value?.status?.name === 'submit' &&
              value?.program &&
              value?.version === 1 &&
              !checkOverdueDatetime(value?.program?.organizationPrequalificationDeadline) && (
                <Can do="request_update" on="pub_project_proposal">
                  <Button
                    icon="pi pi-file-edit"
                    severity="warning"
                    tooltip={
                      value?.isEditable === 1
                        ? t('pubProjectProposal.requestUpdate.addUpdateRequest')
                        : t('pubProjectProposal.requestUpdate.title')
                    }
                    tooltipOptions={{ position: 'left' }}
                    onClick={() => {
                      dialogRequestUpdateRef.current.open({ mode: 'requestUpdate', value });
                    }}
                  />
                </Can>
              )} */}

          {/* Xem lịch sử trạng thái của đề tài */}
          <Can do="read" on="pub_project_proposal">
            <Button
              icon="pi pi-history"
              tooltip={t('pubProjectProposal.viewList.tooltip.viewHistory')}
              tooltipOptions={{ position: 'left' }}
              onClick={() => {
                setProjectHistory(value?.projectLogs ?? []);
                setViewHistoryVisible(true);
              }}
            />
          </Can>
          {/* Xem lịch sử gửi email thông báo */}
          <Can do="read" on="pub_project_proposal">
            <Button
              style={{ paddingLeft: '0.95rem', paddingRight: '0.95rem' }}
              severity="success"
              tooltip={t('pubProjectProposal.viewList.tooltip.viewEmailLogs')}
              tooltipOptions={{ position: 'left' }}
              onClick={() => {
                setProjectProposalId(value?.id ?? '');
                setViewEmailLogsVisible(true);
              }}
            >
              <i className="pi pi-envelope p-overlay-badge">
                {value?.emailLogs?.length > 0 && <Badge value={value?.emailLogs?.length} />}
              </i>
            </Button>
          </Can>
          {/* Duyệt đề tài */}
          {/* {value?.status?.name === 'submit' &&
              // chưa tạo hội đồng nào hoặc (đã Đạt Hội đồng tài chính + không còn yc giải trình)
              ((value?.version === 1 && !value?.projectProposalCouncils?.length) ||
                (value?.version === 4 &&
                  value?.projectProposalCouncils?.length === 3 &&
                  value?.projectProposalCouncils[2]?.councilType?.code === 'financeCouncil' &&
                  value?.projectProposalCouncils[2]?.councilStatus === 'qualified' &&
                  !financeReviseLog?.isOpened)) &&
              // chuyên viên đơn vị được duyệt đề tài C, chuyên viên ban/admin được duyệt hết
              ((value?.projectType === 'C' && ability.can('read', 'pub_project_proposal')) ||
                ability.can('approve', 'pub_project_proposal')) && (
                <Button
                  icon="pi pi-check"
                  severity="success"
                  tooltip={t('pubProjectProposal.viewList.approve')}
                  tooltipOptions={{ position: 'left' }}
                  onClick={() =>
                    dialogConfirmRef.current.open({ mode: 'approve', mutate: mutateApprove, value })
                  }
                />
              )} */}
          {/* Tạo hợp đồng */}
          {/* {value?.status?.name === 'approved' && (
              <Can do="create" on="project">
                <Link
                  to={{
                    pathname: `/projects-submit/create/${value?.id}`,
                  }}
                >
                  <Button
                    icon="pi pi-pencil"
                    severity="info"
                    tooltip={t('pubProjectProposal.viewList.createContract')}
                    tooltipOptions={{ position: 'left' }}
                  />
                </Link>
              </Can>
            )} */}
          {/* Xóa đề tài */}
          <Can do="delete" on="pub_project_proposal">
            <Button
              icon="pi pi-trash"
              severity="danger"
              tooltip={t('pubProjectProposal.viewList.delete')}
              tooltipOptions={{ position: 'left' }}
              onClick={() =>
                dialogConfirmRef.current.open({ mode: 'delete', mutate: mutateDelete, value })
              }
            />
          </Can>
          {/* Duyet de tai */}
          {value?.status?.name === 'pendingReview' && (
            <Can do="read" on="pub_project_proposal">
              <Button
                icon="pi pi-check"
                severity="success"
                tooltip={t('pubProjectProposal.viewList.approve')}
                tooltipOptions={{ position: 'left' }}
                onClick={() =>
                  dialogConfirmRef.current.open({ mode: 'approve', mutate: mutateApprove, value })
                }
              />
            </Can>
          )}
        </>
      ) : (
        <Can do="restore" on="pub_project_proposal">
          {/* Khôi phục đề tài */}
          <Button
            icon="pi pi-undo"
            severity="danger"
            tooltip={t('pubProjectProposal.viewList.restore')}
            tooltipOptions={{ position: 'left' }}
            onClick={() =>
              dialogConfirmRef.current.open({ mode: 'restore', mutate: mutateRestore, value })
            }
          />
        </Can>
      )}
    </div>
  );
  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        handleAllRowUnselect();
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        handleAllRowUnselect();
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(filters)
        );
      }}
      disabled={
        !totalExpenditureSchema.isValidSync({
          min: filters?.totalExpenditure?.min ?? undefined,
          max: filters?.totalExpenditure?.max ?? undefined,
        })
      }
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  const renderHeader = () => (
    <div className="flex justify-content-between">
      <Button
        type="button"
        icon="pi pi-filter"
        label={t('pubProjectProposal.viewList.searchFilters')}
        outlined
        onClick={() => setIsVisibleSearchFilters(true)}
      />
    </div>
  );

  if (isError) {
    return <h4 className="text-red-500">{mapError(t, error)}</h4>;
  }

  return (
    <div className="card relative">
      <div className="flex align-items-center justify-content-between gap-2 pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">
          {t('pubProjectProposal.viewList.title')}
        </h4>
        <div className="flex gap-2">
          <Can do="export_excel" on="project_proposal">
            <div>
              <Button
                type="button"
                label={t('pubProjectProposal.viewList.tooltip.excel')}
                icon={classNames(
                  'pi',
                  { 'pi-spin pi-spinner': isExportExcelLoading },
                  { 'pi-file-excel': !isExportExcelLoading }
                )}
                severity="success"
                onClick={handleExportExcel}
                disabled={
                  (!isSelectedAll && !isUnselectedSome && !selectedItems.length) ||
                  isExportExcelLoading
                }
              />
            </div>
          </Can>
          <Can do="read" on="project_proposal">
            <div>
              <Button
                type="button"
                label={t('pubProjectProposal.viewList.tooltip.download')}
                icon={classNames(
                  'pi',
                  { 'pi-spin pi-spinner': isDownloadZipLoading },
                  { 'pi pi-download': !isDownloadZipLoading }
                )}
                severity="info"
                onClick={handleDownloadZip}
                disabled={
                  (!isSelectedAll && !isUnselectedSome && !selectedItems.length) ||
                  isDownloadZipLoading
                }
              />
            </div>
          </Can>
          <Can do="read" on="project_proposal">
            <div>
              <Button
                type="button"
                label={t('pubProjectProposal.viewList.tooltip.sendRemindEmail')}
                icon={classNames(
                  'pi',
                  { 'pi-spin pi-spinner': isSendRemindEmailLoading },
                  { 'pi-envelope': !isSendRemindEmailLoading }
                )}
                onClick={() => {
                  dialogSendMailRef.current.open();
                }}
                disabled={
                  (!isSelectedAll && !isUnselectedSome && !selectedItems.length) ||
                  isSendRemindEmailLoading
                }
              />
            </div>
          </Can>
        </div>
      </div>

      {/* Custom checkbox selectAll */}
      {!isLoading && data?.data?.length ? (
        <Checkbox
          style={{ position: 'absolute', top: '200px', left: '43px', zIndex: 10 }}
          checked={isSelectedAll}
          onChange={() => (!isSelectedAll ? handleAllRowSelect() : handleAllRowUnselect())}
        />
      ) : null}

      <DataTable
        value={data?.data}
        className="project-list"
        dataKey="id"
        header={renderHeader}
        showGridlines
        scrollable
        selectionMode="multiple"
        selection={selectedItems}
        showSelectAll={false}
        onRowSelect={handleRowSelect}
        onRowUnselect={handleRowUnselect}
        isDataSelectable={isRowSelectable}
        rowClassName={rowClassName}
        filters={filtersTable}
        onFilter={(e) => setFiltersTable(e.filters)}
        filterDisplay="menu"
        loading={isLoading}
        emptyMessage={t('pubProjectProposal.viewList.emptyMessage')}
      >
        {ability.can('export_excel', 'project_proposal') ? (
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} frozen />
        ) : null}
        <Column
          header={t('pubProjectProposal.viewList.name')}
          headerStyle={filterActiveStyle('vietnameseName')}
          body={formatName}
          filter
          filterField="_vietnameseName"
          filterElement={vietnameseNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, vietnameseName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', maxWidth: '16rem' }}
          frozen
          alignFrozen="left"
        />
        <Column
          field="projectType"
          headerStyle={{ ...filterActiveStyle('projectType'), justifyContent: 'center' }}
          header={t('pubProjectProposal.viewList.projectType')}
          filter
          filterField="_projectType"
          filterElement={projectTypeFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, projectType: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '6rem', textAlign: 'center' }}
        />
        <Column
          header={t('pubProjectProposal.viewList.owner')}
          headerStyle={{ ...filterActiveStyle('ownerName'), justifyContent: 'center' }}
          body={formatOwner}
          filter
          filterField="_ownerName"
          filterElement={ownerNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, ownerName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        {/* <Column
          header={t('pubProjectProposal.viewList.organization')}
          headerStyle={{ ...filterActiveStyle('organizationId'), justifyContent: 'center' }}
          body={formatOrganization}
          filter
          filterField="_organization"
          filterElement={organizationFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu={organizations?.length > 1}
          onFilterClear={() => {
            const newFilters = { ...filters, organizationId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        /> */}
        <Column
          header={t('pubProjectProposal.viewList.researchField')}
          headerStyle={{ ...filterActiveStyle('researchFieldId'), justifyContent: 'center' }}
          body={formatResearchFields}
          filter
          filterField="_researchField"
          filterElement={researchFieldFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, researchFieldId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', textAlign: 'center' }}
        />
        <Column
          field="researchType"
          header={t('pubProjectProposal.viewList.researchType')}
          headerStyle={{ ...filterActiveStyle('researchTypeId'), justifyContent: 'center' }}
          body={formatResearchType}
          filter
          filterField="_researchType"
          filterElement={researchTypeFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, researchTypeId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        <Column
          field="implementationMonths"
          header={t('pubProjectProposal.viewList.implementationTime')}
          dataType="numeric"
          style={{ minWidth: '6rem', textAlign: 'center' }}
        />
        <Column
          field="totalExpenditure"
          header={t('pubProjectProposal.viewList.totalExpenditure')}
          headerStyle={{ ...filterActiveStyle('totalExpenditure'), justifyContent: 'center' }}
          body={formatTotalExpenditure}
          filter
          filterField="_totalExpenditure"
          filterElement={totalExpenditureFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, totalExpenditure: {} };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            const isValid = totalExpenditureSchema.isValidSync({
              min: filters?.totalExpenditure?.min ?? undefined,
              max: filters?.totalExpenditure?.max ?? undefined,
            });
            if (isValid) {
              setAppliedFilters(filters);
            }
          }}
          dataType="numeric"
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        <Column
          field="program.name"
          header={t('pubProjectProposal.viewList.program')}
          headerStyle={filterActiveStyle('programId')}
          dataType="date"
          filter
          filterField="_program"
          filterElement={programFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, programId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem' }}
        />
        <Column
          header={t('pubProjectProposal.viewList.submittedAt')}
          headerStyle={filterActiveStyle('submittedAt')}
          body={formatSubmittedAt}
          dataType="date"
          filter
          filterField="_submittedAt"
          filterElement={submittedAtFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = {
              ...filters,
              submittedAt: {},
            };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '4rem' }}
        />
        {/* <Column
          header={t('pubProjectProposal.viewList.council')}
          headerStyle={{
            ...filterActiveStyle('councilTypeId'),
            textAlign: 'center',
          }}
          body={formatCouncil}
          filter
          filterField="_councilType"
          filterElement={councilTypeFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = {
              ...filters,
              councilTypeId: [],
            };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '10rem', maxWidth: '16rem' }}
        /> */}
        <Column
          field="status.name"
          header={t('pubProjectProposal.viewList.status')}
          headerStyle={filterActiveStyle('statusName')}
          body={formatStatus}
          filter
          filterField="_status"
          filterElement={statusFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, statusName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ minWidth: '8rem', textAlign: 'center' }}
        />
        <Column
          header={t('pubProjectProposal.action')}
          body={formatActions}
          style={{ maxWidth: '10rem', textAlign: 'center' }}
          frozen
          alignFrozen="right"
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data.pagination}
        showMode={showMode}
        setShowMode={setShowMode}
        pageName="pub-projects-revise"
      />

      {isVisibleSearchFilters && (
        <SearchFilters
          visible={isVisibleSearchFilters}
          setVisible={setIsVisibleSearchFilters}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          projectTypes={projectTypes}
          // organizations={organizations}
          researchFields={researchFields}
          researchTypes={researchTypes}
          programs={programs}
          // councilTypes={councilTypes}
          statuses={statuses}
        />
      )}

      <Menu model={approvalItems} popup ref={approvalMenu} />

      <ViewHistory
        data={projectHistory}
        visible={viewHistoryVisible}
        setVisible={setViewHistoryVisible}
      />

      <ViewEmailLogs
        projectProposalId={projectProposalId}
        visible={viewEmailLogsVisible}
        setVisible={setViewEmailLogsVisible}
      />

      <SendMail
        ref={dialogSendMailRef}
        handleSendMail={handleSendMail}
        isLoading={isSendRemindEmailLoading}
      />
      <Confirm ref={dialogConfirmRef} refetch={refetch} />
      <RequestUpdateForm ref={dialogRequestUpdateRef} />

      <CreateContractForm ref={dialogCreateContractRef} />

      <ErrorDialog
        title={t('pubProjectProposal.exportExcelErrorTitle')}
        isError={isExportExcelError}
        error={exportExcelError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
      <ErrorDialog
        title={t('pubProjectProposal.downloadZipErrorTitle')}
        isError={isDownloadZipError}
        error={downloadZipError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
      <ErrorDialog
        title={t('pubProjectProposal.sendRemindEmailErrorTitle')}
        isError={isSendRemindEmailError}
        error={sendRemindEmailError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
    </div>
  );
  // #endregion Render
}

export default RevisePubProjectProposalViewList;
